import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Modal } from '@shared/ui/Modal';
import { Button } from '@shared/ui/Button';
import { Field, Input, Label } from '@shared/ui/Input';
import {
  useCheckPolyusLetterMutation,
  useSendPolyusLetterMutation,
  type PolyusCodeEntry,
  type PolyusLetterCheck,
  type PolyusRules,
} from '@shared/api';

/**
 * Отправка письма с шифром ООО «Полюс Сухой Лог» прямо из ОСНОВЫ: PDF
 * письма (уйдёт под именем шифра), приложения, текст e-mail. Письмо и
 * приложения вместе — не больше 9 МБ, иначе приложения в VDR и ссылка на
 * папку. Перед отправкой — отдельное подтверждение: письмо уходит Заказчику
 * и отозвать его нельзя.
 */

const LIMIT_BYTES = 9 * 1024 * 1024;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const Route = styled.div`
  padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(3)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  font-size: 13px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.secondary};

  b {
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: 600;
  }
`;

const Mono = styled.code`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.text.primary};
  overflow-wrap: anywhere;
`;

const FileRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};

  span {
    overflow-wrap: anywhere;
  }
`;

const Size = styled.span`
  flex-shrink: 0;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Note = styled.div<{ $tone?: 'ok' | 'warn' | 'error' }>`
  font-size: 12.5px;
  line-height: 1.5;
  color: ${({ $tone, theme }) =>
    $tone === 'ok'
      ? theme.colors.status.success
      : $tone === 'warn'
        ? theme.colors.status.warning
        : $tone === 'error'
          ? theme.colors.status.danger
          : theme.colors.text.muted};
`;

const Meter = styled.div`
  height: 6px;
  border-radius: 3px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  overflow: hidden;
`;

const MeterFill = styled.div<{ $pct: number; $over: boolean }>`
  width: ${({ $pct }) => Math.min(100, $pct)}%;
  height: 100%;
  background: ${({ $over, theme }) => ($over ? theme.colors.status.danger : theme.colors.primary)};
`;

const Body = styled.textarea`
  min-height: 190px;
  resize: vertical;
  padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(3)}`};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text.primary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  font: inherit;
  font-size: 13px;
  line-height: 1.5;
  outline: none;
  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const Check = styled.label`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing(2)};
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;

  input {
    margin-top: 3px;
  }
`;

const Confirm = styled.div`
  padding: ${({ theme }) => theme.spacing(3)};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.status.warning}15;
  border: 1px solid ${({ theme }) => theme.colors.status.warning}55;
  font-size: 13px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
  margin-top: ${({ theme }) => theme.spacing(4)};
`;

const Hidden = styled.input`
  display: none;
`;

/** «1 файл», «2 файла», «5 файлов». */
function filesWord(n: number): string {
  const d = n % 10;
  const dd = n % 100;
  const word =
    d === 1 && dd !== 11 ? 'файл' : d >= 2 && d <= 4 && (dd < 12 || dd > 14) ? 'файла' : 'файлов';
  return `${n} ${word}`;
}

function mb(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} МБ`;
}

function errorMessage(e: unknown): string {
  const data = (e as { data?: { error?: { message?: string }; message?: string } })?.data;
  return data?.error?.message ?? data?.message ?? 'Письмо не отправлено — попробуйте ещё раз';
}

function template(
  entry: PolyusCodeEntry,
  attachments: string[],
  vdrLink: string | null,
): string {
  const lines = [
    'Добрый день!',
    '',
    `Направляем письмо АО «САТКОМ» ${entry.code} «${entry.subject}»${
      entry.reply_to ? ` (на № ${entry.reply_to})` : ''
    }.`,
    '',
    'Вложения:',
    `1. ${entry.code}.pdf — письмо`,
    ...attachments.map((a, i) => `${i + 2}. ${a}`),
  ];
  if (vdrLink !== null) {
    lines.push(
      '',
      `Приложения размещены в VDR в папке «${entry.code}»: ${vdrLink || '<ссылка на папку>'}`,
    );
  }
  lines.push('', 'С уважением,', 'АО «САТКОМ»');
  return lines.join('\n');
}

interface Props {
  entry: PolyusCodeEntry;
  rules: PolyusRules | undefined;
  onClose: () => void;
}

export function PolyusSendModal({ entry, rules, onClose }: Props) {
  const [letter, setLetter] = useState<File | null>(null);
  const [check, setCheck] = useState<PolyusLetterCheck | null>(null);
  const [checkLetter, checkState] = useCheckPolyusLetterMutation();
  const [attachments, setAttachments] = useState<File[]>([]);
  const [vdr, setVdr] = useState(false);
  const [vdrLink, setVdrLink] = useState('');
  const [body, setBody] = useState('');
  const [bodyEdited, setBodyEdited] = useState(false);
  const [blankChecked, setBlankChecked] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [sent, setSent] = useState<PolyusCodeEntry | null>(null);
  const [send, sendState] = useSendPolyusLetterMutation();
  const letterInput = useRef<HTMLInputElement>(null);
  const attInput = useRef<HTMLInputElement>(null);

  const sending = rules?.sending;
  const from = sending?.from_email ?? '—';
  const to = sending?.to ?? 'slgold@polyus.com';
  const mailFiles = vdr ? [] : attachments;
  const total = (letter?.size ?? 0) + mailFiles.reduce((s, f) => s + f.size, 0);
  const over = total > LIMIT_BYTES;
  const generated = useMemo(
    () =>
      template(
        entry,
        vdr ? [] : attachments.map((f) => f.name),
        vdr ? vdrLink.trim() : null,
      ),
    [entry, attachments, vdr, vdrLink],
  );

  useEffect(() => {
    if (!bodyEdited) setBody(generated);
  }, [generated, bodyEdited]);

  async function pickLetter(file: File | null) {
    setLetter(file);
    setCheck(null);
    setBlankChecked(false);
    if (!file) return;
    const form = new FormData();
    form.append('letter', file);
    try {
      setCheck(await checkLetter({ id: entry.id, form }).unwrap());
    } catch {
      setCheck({ pdf: true, checked: false, size_bytes: file.size });
    }
  }

  const isPdf = check ? check.pdf : true;
  const codeOk = Boolean(check?.checked && check.code_found);
  const needsBlankAck = Boolean(letter && check && check.pdf && !codeOk);
  const vdrOk = !vdr || /^https?:\/\/\S+$/i.test(vdrLink.trim());
  const ready =
    Boolean(sending?.enabled) &&
    Boolean(letter) &&
    isPdf &&
    !checkState.isLoading &&
    (!needsBlankAck || blankChecked) &&
    !over &&
    vdrOk &&
    body.trim().length > 0;

  async function doSend() {
    if (!letter || !ready) return;
    const form = new FormData();
    form.append('letter', letter);
    for (const f of mailFiles) form.append('attachment', f);
    form.append('body', body);
    if (vdr) form.append('vdr_link', vdrLink.trim());
    try {
      setSent(await send({ id: entry.id, form }).unwrap());
    } catch {
      setConfirming(false);
    }
  }

  if (sent) {
    return (
      <Modal open title={`Письмо ${sent.code} отправлено`} onClose={onClose}>
        <Route>
          Отправлено{' '}
          <b>{sent.sent_at ? new Date(sent.sent_at).toLocaleString('ru-RU') : ''}</b> на{' '}
          <b>{sent.sent_to ?? to}</b> с <b>{sent.sent_from ?? from}</b>.
          <br />
          Копия появится в «Отправленных» ящика {sent.sent_from ?? from} в течение пары минут.
          Письмо и файлы открываются из журнала шифров кнопкой «Письмо».
        </Route>
        {(sent.warnings ?? []).map((w) => (
          <Note key={w} $tone="warn">
            {w}
          </Note>
        ))}
        <Actions>
          <Button type="button" onClick={onClose}>
            Готово
          </Button>
        </Actions>
      </Modal>
    );
  }

  return (
    <Modal
      open
      title={`Отправить письмо ${entry.code}`}
      onClose={() => !sendState.isLoading && onClose()}
    >
      <Section>
        <Route>
          Кому: <b>{to}</b> — официальный адрес Заказчика
          <br />
          От: <b>АО «САТКОМ»</b> &lt;{from}&gt;
          <br />
          Тема: <Mono>{`${entry.code} ${entry.subject}`}</Mono>
        </Route>
        {sending && !sending.enabled && (
          <Note $tone="error">
            Письмо не уйдёт: {sending.blocked_reason ?? 'отправка почты на сервере не настроена'}.
          </Note>
        )}
      </Section>

      <Section>
        <Label>Письмо (PDF с шифром под темой) *</Label>
        <Hidden
          ref={letterInput}
          type="file"
          accept="application/pdf,.pdf"
          onChange={(e) => {
            void pickLetter(e.target.files?.[0] ?? null);
            e.target.value = '';
          }}
        />
        {letter ? (
          <FileRow>
            <span>
              {letter.name} → <Mono>{entry.code}.pdf</Mono>
            </span>
            <Size>{mb(letter.size)}</Size>
            <Button type="button" $size="sm" $variant="ghost" onClick={() => void pickLetter(null)}>
              Убрать
            </Button>
          </FileRow>
        ) : (
          <div>
            <Button type="button" $variant="secondary" onClick={() => letterInput.current?.click()}>
              Выбрать PDF письма
            </Button>
          </div>
        )}
        {checkState.isLoading && <Note>Проверяем шифр на бланке…</Note>}
        {letter && check && !check.pdf && (
          <Note $tone="error">Файл письма должен быть в PDF.</Note>
        )}
        {letter && codeOk && <Note $tone="ok">Шифр {entry.code} на бланке найден.</Note>}
        {letter && check?.pdf && check.checked && check.text_found && !check.code_found && (
          <Note $tone="warn">
            На бланке не найден шифр {entry.code}
            {check.other_codes?.length ? ` — в письме стоит ${check.other_codes.join(', ')}` : ''}.
            Исправьте бланк или проверьте его глазами.
          </Note>
        )}
        {letter && check?.pdf && check.checked && !check.text_found && (
          <Note $tone="warn">PDF без текста (скан) — проверьте шифр на бланке глазами.</Note>
        )}
        {letter && check?.pdf && !check.checked && (
          <Note $tone="warn">Не удалось прочитать PDF — проверьте шифр на бланке глазами.</Note>
        )}
        {needsBlankAck && (
          <Check>
            <input
              type="checkbox"
              checked={blankChecked}
              onChange={(e) => setBlankChecked(e.target.checked)}
            />
            Я проверил(а): на бланке под темой стоит шифр {entry.code}
          </Check>
        )}
      </Section>

      <Section>
        <Label>Приложения</Label>
        <Hidden
          ref={attInput}
          type="file"
          multiple
          onChange={(e) => {
            const picked = Array.from(e.target.files ?? []);
            setAttachments((prev) => [...prev, ...picked]);
            e.target.value = '';
          }}
        />
        {!vdr &&
          attachments.map((f, i) => (
            <FileRow key={`${f.name}-${i}`}>
              <span>{f.name}</span>
              <Size>{mb(f.size)}</Size>
              <Button
                type="button"
                $size="sm"
                $variant="ghost"
                onClick={() => setAttachments((prev) => prev.filter((_, j) => j !== i))}
              >
                Убрать
              </Button>
            </FileRow>
          ))}
        {!vdr && (
          <div>
            <Button type="button" $variant="secondary" onClick={() => attInput.current?.click()}>
              Добавить файлы
            </Button>
          </div>
        )}
        <Note>
          Файлы называют так, как они названы в тексте письма: «Приложение 1 - …».
        </Note>
        <Meter aria-label="Объём письма">
          <MeterFill $pct={(total / LIMIT_BYTES) * 100} $over={over} />
        </Meter>
        <Note $tone={over ? 'error' : undefined}>
          {mb(total)} из 9 МБ
          {over && ' — больше лимита Заказчика: перенесите приложения в VDR'}
        </Note>
        <Check>
          <input type="checkbox" checked={vdr} onChange={(e) => setVdr(e.target.checked)} />
          Приложения больше 9 МБ — загружаю их в VDR в папку «{entry.code}»
        </Check>
        {vdr && (
          <Field>
            <Label htmlFor="ps-vdr">Ссылка на папку в VDR *</Label>
            <Input
              id="ps-vdr"
              value={vdrLink}
              onChange={(e) => setVdrLink(e.target.value)}
              placeholder="https://…"
            />
            {!vdrOk && vdrLink.trim() && (
              <Note $tone="error">Ссылка должна начинаться с https://</Note>
            )}
            <Note>
              Без письма со ссылкой на {to} файлы в VDR не считаются переданными. Доступ к VDR —
              запросом на {rules?.vdr_access_email ?? 'DCC.SLZ@polyus.com'}.
            </Note>
          </Field>
        )}
      </Section>

      <Section>
        <Label htmlFor="ps-body">Текст письма *</Label>
        <Body
          id="ps-body"
          value={body}
          onChange={(e) => {
            setBody(e.target.value);
            setBodyEdited(true);
          }}
        />
        {bodyEdited && (
          <div>
            <Button
              type="button"
              $size="sm"
              $variant="ghost"
              onClick={() => {
                setBodyEdited(false);
                setBody(generated);
              }}
            >
              Вернуть шаблон
            </Button>
          </div>
        )}
      </Section>

      {sendState.isError && <Note $tone="error">{errorMessage(sendState.error)}</Note>}

      {confirming ? (
        <>
          <Confirm>
            Письмо {entry.code} уйдёт Заказчику на <b>{to}</b> с адреса <b>{from}</b>
            {mailFiles.length > 0
              ? ` — письмо и ${filesWord(mailFiles.length)} приложений`
              : ' — только письмо, без приложений'}
            . Отозвать отправленное письмо нельзя.
          </Confirm>
          <Actions>
            <Button
              type="button"
              $variant="secondary"
              onClick={() => setConfirming(false)}
              disabled={sendState.isLoading}
            >
              Назад
            </Button>
            <Button type="button" onClick={doSend} disabled={!ready || sendState.isLoading}>
              {sendState.isLoading ? 'Отправляем…' : 'Да, отправить Заказчику'}
            </Button>
          </Actions>
        </>
      ) : (
        <Actions>
          <Button type="button" $variant="secondary" onClick={onClose}>
            Отмена
          </Button>
          <Button type="button" onClick={() => setConfirming(true)} disabled={!ready}>
            Отправить в ПОЛЮС
          </Button>
        </Actions>
      )}
    </Modal>
  );
}
